import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  Users, UserPlus, Clock, Play, Square, Euro, Calendar,
  MoreVertical, Edit, Trash2, Check, X, Timer, Coffee,
  TrendingUp, Wallet, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function EmployeesView({ sessionToken }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTimesheetModal, setShowTimesheetModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    pin: '',
    role: 'mitarbeiter',
    hourly_rate: 12.50
  });

  // Fetch employees
  const fetchEmployees = useCallback(async () => {
    if (!sessionToken) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/cashx/employees`, {
        headers: { 'X-Session-Token': sessionToken }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees || []);
      }
    } catch (error) {
      console.error('Fetch employees error:', error);
      // Set demo data if API doesn't exist yet
      setEmployees([
        { 
          employee_id: '1', 
          name: 'Max Mustermann', 
          role: 'mitarbeiter', 
          pin: '1234',
          hourly_rate: 12.50,
          is_working: true, 
          shift_start: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          total_hours_today: 3,
          total_tips_today: 15.50
        },
        { 
          employee_id: '2', 
          name: 'Anna Schmidt', 
          role: 'schichtleiter', 
          pin: '5678',
          hourly_rate: 14.00,
          is_working: true, 
          shift_start: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
          total_hours_today: 5,
          total_tips_today: 22.00
        },
        { 
          employee_id: '3', 
          name: 'Tom Weber', 
          role: 'fahrer', 
          pin: '9012',
          hourly_rate: 13.00,
          is_working: false,
          total_hours_today: 0,
          total_tips_today: 0
        }
      ]);
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Get initials
  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Get role badge
  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-purple-500/20 text-purple-400 border-0">Admin</Badge>;
      case 'schichtleiter':
        return <Badge className="bg-blue-500/20 text-blue-400 border-0">Schichtleiter</Badge>;
      case 'fahrer':
        return <Badge className="bg-amber-500/20 text-amber-400 border-0">Fahrer</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400 border-0">Mitarbeiter</Badge>;
    }
  };

  // Calculate shift duration
  const getShiftDuration = (startTime) => {
    if (!startTime) return '0:00';
    const diff = Date.now() - new Date(startTime).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };

  // Clock in/out
  const toggleShift = async (employeeId, isWorking) => {
    // In real implementation, this would call the API
    setEmployees(prev => prev.map(emp => {
      if (emp.employee_id === employeeId) {
        return {
          ...emp,
          is_working: !isWorking,
          shift_start: !isWorking ? new Date().toISOString() : null
        };
      }
      return emp;
    }));
    
    toast.success(isWorking ? 'Schicht beendet' : 'Schicht gestartet');
  };

  // Add employee
  const handleAddEmployee = async () => {
    if (!formData.name || !formData.pin) {
      toast.error('Name und PIN sind erforderlich');
      return;
    }
    
    // In real implementation, this would call the API
    const newEmployee = {
      employee_id: Date.now().toString(),
      ...formData,
      is_working: false,
      total_hours_today: 0,
      total_tips_today: 0
    };
    
    setEmployees(prev => [...prev, newEmployee]);
    setShowAddModal(false);
    setFormData({ name: '', pin: '', role: 'mitarbeiter', hourly_rate: 12.50 });
    toast.success('Mitarbeiter hinzugefügt');
  };

  // Calculate totals
  const workingCount = employees.filter(e => e.is_working).length;
  const totalHoursToday = employees.reduce((sum, e) => sum + (e.total_hours_today || 0), 0);
  const totalTipsToday = employees.reduce((sum, e) => sum + (e.total_tips_today || 0), 0);

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Mitarbeiter</h1>
          <p className="text-gray-400">
            {employees.length} Mitarbeiter • {workingCount} aktiv
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="bg-amber-600 hover:bg-amber-700">
          <UserPlus className="w-4 h-4 mr-2" />
          Mitarbeiter hinzufügen
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{workingCount}</div>
              <div className="text-xs text-gray-500">Arbeiten gerade</div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{totalHoursToday.toFixed(1)}h</div>
              <div className="text-xs text-gray-500">Stunden heute</div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{totalTipsToday.toFixed(2)} €</div>
              <div className="text-xs text-gray-500">Trinkgeld heute</div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{employees.length}</div>
              <div className="text-xs text-gray-500">Mitarbeiter gesamt</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Employee List */}
      <Card className="bg-[#12121a] border-gray-800 flex-1">
        <CardHeader className="border-b border-gray-800">
          <CardTitle className="text-lg text-white">Alle Mitarbeiter</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[calc(100vh-420px)]">
            {loading ? (
              <div className="p-8 text-center text-gray-400">Lade...</div>
            ) : employees.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Noch keine Mitarbeiter</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {employees.map((employee) => (
                  <div
                    key={employee.employee_id}
                    className="p-4 hover:bg-gray-800/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Avatar */}
                        <Avatar className="w-12 h-12">
                          <AvatarFallback className={`
                            ${employee.is_working ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-400'}
                            font-bold
                          `}>
                            {getInitials(employee.name)}
                          </AvatarFallback>
                        </Avatar>
                        
                        {/* Info */}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white">{employee.name}</span>
                            {getRoleBadge(employee.role)}
                            {employee.is_working && (
                              <Badge className="bg-green-500/20 text-green-400 border-0 animate-pulse">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-400 mr-1"></span>
                                Aktiv
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-gray-400 mt-1">
                            PIN: {employee.pin} • {employee.hourly_rate?.toFixed(2)} €/h
                          </div>
                        </div>
                      </div>
                      
                      {/* Shift Info & Actions */}
                      <div className="flex items-center gap-4">
                        {/* Current Shift */}
                        {employee.is_working && (
                          <div className="text-right">
                            <div className="text-white font-mono text-lg">
                              {getShiftDuration(employee.shift_start)}
                            </div>
                            <div className="text-xs text-gray-500">Schichtdauer</div>
                          </div>
                        )}
                        
                        {/* Today's Stats */}
                        <div className="text-right">
                          <div className="text-amber-400 font-medium">
                            {employee.total_tips_today?.toFixed(2) || '0.00'} €
                          </div>
                          <div className="text-xs text-gray-500">Trinkgeld heute</div>
                        </div>
                        
                        {/* Clock In/Out Button */}
                        <Button
                          onClick={() => toggleShift(employee.employee_id, employee.is_working)}
                          className={`w-28 ${
                            employee.is_working 
                              ? 'bg-red-600 hover:bg-red-700' 
                              : 'bg-green-600 hover:bg-green-700'
                          }`}
                        >
                          {employee.is_working ? (
                            <>
                              <Square className="w-4 h-4 mr-2" />
                              Ausstempeln
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4 mr-2" />
                              Einstempeln
                            </>
                          )}
                        </Button>
                        
                        {/* More Actions */}
                        <Button variant="ghost" size="sm" className="text-gray-400">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Add Employee Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[#12121a] border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-amber-400" />
              Mitarbeiter hinzufügen
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-gray-300">Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Max Mustermann"
                className="bg-gray-800 border-gray-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-gray-300">PIN (4 Ziffern)</Label>
              <Input
                value={formData.pin}
                onChange={(e) => setFormData({...formData, pin: e.target.value.replace(/\D/g, '').slice(0, 4)})}
                placeholder="1234"
                maxLength={4}
                className="bg-gray-800 border-gray-700 text-white mt-1 font-mono text-lg tracking-widest"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Rolle</Label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full mt-1 p-2 bg-gray-800 border border-gray-700 rounded-md text-white"
                >
                  <option value="mitarbeiter">Mitarbeiter</option>
                  <option value="schichtleiter">Schichtleiter</option>
                  <option value="fahrer">Fahrer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <Label className="text-gray-300">Stundenlohn (€)</Label>
                <Input
                  type="number"
                  step="0.50"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({...formData, hourly_rate: parseFloat(e.target.value)})}
                  className="bg-gray-800 border-gray-700 text-white mt-1"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)} className="border-gray-700">
              Abbrechen
            </Button>
            <Button onClick={handleAddEmployee} className="bg-amber-600 hover:bg-amber-700">
              <Check className="w-4 h-4 mr-2" />
              Hinzufügen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
